import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { debounceTime, distinctUntilChanged, map, switchMap, tap } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { Hectometre } from '../classes/hectometre';
import { TravelData } from '../classes/travel-data';
import { ApiService } from './api/api.service';

@Injectable()
export class LocationService {
    codaltAddress: FormattedAddress = {
        formattedAddress: 'Leerparkpromenade 40, 3312 KW Dordrecht, Nederland',
        addressLine: 'Leerparkpromenade 40',
        street: 'Leerparkpromenade',
        housenumber: '40',
        postalCode: '3312 KW',
        zipcode: '3312 KW',
        town: 'Dordrecht',
        locality: 'Dordrecht',
        country: 'Nederland',
        lat: 51.8035,
        lng: 4.6768,
    };

    constructor(private apiService: ApiService) {}

    formatAddress(address: Address) {
        if (!address) {
            return '';
        }

        const streetPart = [address.street, address.housenumber].filter(Boolean).join(' ');
        const cityPart = [address.zipcode, address.town].filter(Boolean).join(' ');

        return [streetPart, cityPart].filter(Boolean).join(', ');
    }

    searchHecto(
        term$: Observable<string | null | undefined>,
        minLength = 3,
        debounceMs = 300
    ): Observable<Hectometre[]> {
        let lastExecutedSearch = '';
        let lastExecutedAt = 0;
        let lastResult: Hectometre[] = [];

        return term$.pipe(
            map(value => (value ?? '').trim().replace(/\s+/g, ' ')),
            debounceTime(debounceMs),
            distinctUntilChanged(),
            switchMap(search => {
                if (!search || search.length < minLength) {
                    lastExecutedSearch = '';
                    lastExecutedAt = 0;
                    lastResult = [];

                    return of([]);
                }

                if (!this.shouldExecuteSearch(search, lastExecutedSearch, lastExecutedAt)) {
                    return of(lastResult);
                }

                lastExecutedSearch = search;
                lastExecutedAt = Date.now();

                return this.apiService.postCall$<Hectometre[]>('hectopins/search', { search }).pipe(
                    map(res => res.data),
                    tap(result => {
                        lastResult = result;
                    })
                );
            })
        );
    }

    private getLocations(term: string, source: string = 'unknown'): Observable<FormattedAddress[]> {
        return this.apiService.getCallData$<GoogleGeocodingResponse>('geocode/geocode', { address: term, source }).pipe(
            map(res => {
                return res.results.map(r => {
                    const street = r.address_components.find(ac => ac.types.includes('route'))?.long_name;
                    const houseNumber = r.address_components.find(ac => ac.types.includes('street_number'))?.long_name;
                    const zipcode = r.address_components.find(ac => ac.types.includes('postal_code'))?.long_name;
                    const town = r.address_components.find(ac => ac.types.includes('locality'))?.long_name;
                    const country = r.address_components.find(ac => ac.types.includes('country'))?.long_name;

                    return {
                        formattedAddress: r.formatted_address,
                        addressLine: `${street} ${houseNumber}`,
                        street,
                        housenumber: houseNumber,
                        postalCode: zipcode,
                        zipcode,
                        town,
                        locality: town,
                        country,
                        lat: r.geometry.location.lat,
                        lng: r.geometry.location.lng,
                    } as FormattedAddress;
                });
            })
        );
    }

    searchLocations(
        term$: Observable<string | null | undefined>,
        source: string,
        minLength = 3,
        debounceMs = 300
    ): Observable<FormattedAddress[]> {
        let lastExecutedSearch = '';
        let lastExecutedAt = 0;
        let lastResult: FormattedAddress[] = [];

        return term$.pipe(
            map(value => (value ?? '').trim().replace(/\s+/g, ' ')),
            debounceTime(debounceMs),
            distinctUntilChanged(),
            switchMap(search => {
                if (!search) {
                    lastExecutedSearch = '';
                    lastExecutedAt = 0;
                    lastResult = this.getDefaultLocations();

                    return of(lastResult);
                }

                if (search.length < minLength) {
                    lastExecutedSearch = '';
                    lastExecutedAt = 0;
                    lastResult = [];

                    return of([]);
                }

                if (!this.shouldExecuteSearch(search, lastExecutedSearch, lastExecutedAt)) {
                    return of(lastResult);
                }

                lastExecutedSearch = search;
                lastExecutedAt = Date.now();

                return this.getLocations(search, source).pipe(tap(result => (lastResult = result)));
            })
        );
    }

    getDefaultLocations(): FormattedAddress[] {
        if (environment.production && !environment.test) {
            return [];
        }

        return [this.codaltAddress];
    }

    getLocationsLatLng(
        lat: string | number,
        lng: string | number,
        source: string = 'unknown'
    ): Observable<FormattedAddress[]> {
        return this.apiService
            .getCallData$<GoogleGeocodingResponse>('geocode/reverse-geocode', { lat, lng, source })
            .pipe(
                map(res => {
                    return res.results
                        .filter(qq => qq.types.includes('street_address'))
                        .map(r => {
                            const street = r.address_components.find(ac => ac.types.includes('route'))?.long_name;
                            const houseNumber = r.address_components.find(ac =>
                                ac.types.includes('street_number')
                            )?.long_name;
                            const zipcode = r.address_components.find(ac =>
                                ac.types.includes('postal_code')
                            )?.long_name;
                            const town = r.address_components.find(ac => ac.types.includes('locality'))?.long_name;
                            const country = r.address_components.find(ac => ac.types.includes('country'))?.long_name;

                            return {
                                formattedAddress: r.formatted_address,
                                addressLine: `${street} ${houseNumber}`,
                                street,
                                housenumber: houseNumber,
                                postalCode: zipcode,
                                zipcode,
                                town,
                                locality: town,
                                country,
                                lat: r.geometry.location.lat,
                                lng: r.geometry.location.lng,
                            } as FormattedAddress;
                        });
                })
            );
    }

    autoRoute(from: string, to: string) {
        return this.apiService.getCall$<TravelData>('location/auto', { from, to });
    }

    /**
     * Determines whether a new backend request should be executed.
     *
     * Decisions:
     * - If there is no previous search → execute.
     * - If the search term is exactly the same → skip.
     * - If the term changed to something unrelated → execute immediately.
     * - If an incremental change differs by 2 or more characters → execute.
     * - Otherwise, execute only if the last call was more than 2 seconds ago.
     */
    private shouldExecuteSearch(search: string, last: string, lastAt: number): boolean {
        if (!last) {
            return true;
        }

        if (search === last) {
            return false;
        }

        const isRelatedChange = search.startsWith(last) || last.startsWith(search);
        const hasSignificantLengthChange = Math.abs(search.length - last.length) >= 2;

        return !isRelatedChange || hasSignificantLengthChange || Date.now() - lastAt > 2000;
    }
}

export interface Address {
    street?: string;
    housenumber?: string;
    zipcode?: string;
    town?: string;
}

export interface FormattedAddress {
    formattedAddress: string;
    addressLine: string;
    street: string;
    housenumber: string;
    postalCode: string;
    zipcode: string;
    town: string;
    locality: string;
    country: string;
    lat: number;
    lng: number;
}

export function isAddress(address: any): address is Address {
    return (
        typeof address === 'object' &&
        address !== null &&
        'street' in address &&
        'housenumber' in address &&
        'zipcode' in address &&
        'town' in address &&
        address.street &&
        address.housenumber &&
        address.zipcode &&
        address.town
    );
}

export interface GoogleGeocodingResponse {
    results: GoogleGeocodingResult[];
    status: string;
}

export interface GoogleGeocodingResult {
    address_components: AddressComponent[];
    formatted_address: string;
    geometry: {
        location: {
            lat: number;
            lng: number;
        };
        location_type: string;
        viewport: {
            northeast: LatLng;
            southwest: LatLng;
        };
    };
    place_id: string;
    types: string[];
}

export interface AddressComponent {
    long_name: string;
    short_name: string;
    types: string[];
}

export interface LatLng {
    lat: number;
    lng: number;
}
